"use client";

import { useCepAutocomplete, useParams } from "@/hooks";
import { useApiReady } from "@/hooks/useApiReady";
import { usePermission } from "@/hooks/usePermission/usePermission";
import { AppDispatch } from "@/store";
import {
  createClient,
  deleteClient,
  fetchClients,
  updateClient,
} from "@/store/modules/clients/thunks";
import { fetchGroups } from "@/store/modules/groups/thunks";
import { clientSchema } from "@/validations";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

const useClient = (onSuccess?: () => void) => {
  const { params, setParams, handlePage, updateParams } = useParams();
  const { hasPermission } = usePermission();
  const { isReady: isApiReady } = useApiReady();
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [searchValue, setSearchValue] = useState<string>(params.search || "");
  const [removeExistingImage, setRemoveExistingImage] =
    useState<boolean>(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    getValues,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(clientSchema),
    defaultValues: {
      full_name: "",
      cpf: "",
      email: "",
      phone: "",
      birth_date: undefined,
      zip_code: "",
      street: "",
      address_number: "",
      neighborhood: "",
      city: "",
      country: "",
      state: "",
      complement: "",
      client_group_id: undefined,
      password: "",
      password_confirmation: "",
    },
  });

  const cepValue = watch("zip_code") ?? "";

  useCepAutocomplete(
    cepValue,
    (address) => {
      reset(
        {
          ...getValues(),
          street: address.street,
          neighborhood: address.neighborhood,
          city: address.city,
          state: address.state,
          country: address.country,
        },
        {
          keepErrors: true,
          keepDirty: true,
          keepTouched: true,
        },
      );
    },
    () => {
      reset(
        {
          ...getValues(),
          street: "",
          neighborhood: "",
          city: "",
          state: "",
          country: "",
        },
        {
          keepErrors: true,
          keepDirty: true,
          keepTouched: true,
        },
      );
    },
  );

  const setFormData = (data: Partial<typeof clientSchema.__outputType>) => {
    reset(data);
  };

  const onSubmit = async (data: typeof clientSchema.__outputType) => {
    try {
      let result;
      const buildFormData = (root: typeof clientSchema.__outputType) => {
        const fd = new FormData();
        Object.entries(root).forEach(([k, v]) => {
          if (v === undefined || v === null || v === "") return;
          fd.append(k, String(v));
        });
        if (pendingImageFile) fd.append("image", pendingImageFile);
        return fd;
      };

      if (clientSlug && hasPermission("update client")) {
        if (pendingImageFile) {
          const formData = buildFormData(data);
          result = await dispatch(
            updateClient({
              slug: clientSlug,
              client: formData,
            } as any),
          );
        } else {
          result = await dispatch(
            updateClient({ slug: clientSlug, client: data }),
          );
        }
      } else {
        if (hasPermission("create client")) {
          if (pendingImageFile) {
            const formData = buildFormData(data);
            result = await dispatch(createClient(formData as any));
          } else {
            result = await dispatch(createClient(data));
          }
        }
      }

      if (
        createClient.fulfilled.match(result) ||
        updateClient.fulfilled.match(result)
      ) {
        if (onSuccess) onSuccess();
        reset();
        setClientSlug(null);
        setPendingImageFile(null);
      }
    } catch (error) {
      console.error("Erro ao enviar dados:", error);
    }
  };

  const { clients, loading, error, fetched } = useSelector(
    (state: any) => state.clients,
  );
  const dispatch: AppDispatch = useDispatch();

  const [clientSlug, setClientSlug] = useState<any>();
  const [showInputPassword, setShowInputPassword] = useState<any>();

  useEffect(() => {
    if (!isApiReady) return;

    if (hasPermission("read client")) {
      dispatch(fetchClients(params));
    }
    if (hasPermission("read group")) {
      dispatch(fetchGroups(params));
    }
  }, [params, isApiReady, dispatch]);

  // Debounce para busca
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchValue !== params.search) {
        updateParams({
          search: searchValue,
          current_page: 1,
        });
        setCurrentPage(1);
      }
    }, 500); // 500ms de debounce

    return () => clearTimeout(timeoutId);
  }, [searchValue, updateParams, params.search]);

  const totalItems = clients?.total ?? 0;
  const itemsPerPage = clients?.per_page ?? 10;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / itemsPerPage) : 1;
  const pagesArray = Array.from({ length: totalPages }, (_, i) => i + 1);

  const [currentPage, setCurrentPage] = useState(clients?.current_page ?? 1);

  useEffect(() => {
    if (clients?.current_page && clients.current_page !== currentPage) {
      setCurrentPage(clients.current_page);
    }
  }, [clients?.current_page]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    handlePage(page);
  };

  const handleFilterChange = (value: string) => {
    setSearchValue(value);
  };

  const handleDelete = async () => {
    try {
      if (clientSlug && hasPermission("delete client")) {
        await dispatch(deleteClient(clientSlug));
        setClientSlug(null);

        return true;
      }
    } catch (error) {
      console.error("Error deleting client:", error);
    }
  };

  return {
    register,
    handleSubmit,
    onSubmit,
    reset,
    control,
    errors,
    isSubmitting,
    clients,
    loading,
    error,
    setClientSlug,
    handleDelete,
    setFormData,
    showInputPassword,
    setShowInputPassword,
    handlePage,
    currentPage,
    handlePageChange,
    totalPages,
    itemsPerPage,
    pagesArray,
    setParams,
    handleFilterChange,
    searchValue,
    pendingImageFile,
    setPendingImageFile,
    clientSlug,
    removeExistingImage,
    setRemoveExistingImage,
  };
};

export default useClient;
